import { useCallback, useEffect, useMemo, useState } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import {
  DocumentTemplateCategoryLabel,
  type DocumentTemplateDto,
  type DocumentTemplateListResponse,
  type RenderFormatT,
} from '@toga/shared'

interface Props {
  caseId: string
  onClose: () => void
  onApplied: () => void | Promise<void>
}

export default function ApplyTemplateModal({ caseId, onClose, onApplied }: Props) {
  const [templates, setTemplates] = useState<DocumentTemplateDto[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [preview, setPreview] = useState('')
  const [format, setFormat] = useState<RenderFormatT>('pdf')
  const [fileName, setFileName] = useState('')
  const [loading, setLoading] = useState(true)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<DocumentTemplateListResponse>('/document-templates')
      .then((r) => {
        setTemplates(r.items)
        if (r.items.length > 0) setSelectedId(r.items[0]!.id)
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error'))
      .finally(() => setLoading(false))
  }, [])

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId]
  )

  // Render preview con datos reales del expediente.
  const reloadPreview = useCallback(async () => {
    if (!selectedId) return
    setPreviewLoading(true)
    try {
      const r = await api.post<{ rendered: string }>(
        `/document-templates/${selectedId}/preview`,
        { caseId }
      )
      setPreview(r.rendered)
    } catch (err) {
      setPreview('(error al generar vista previa)')
      void err
    } finally {
      setPreviewLoading(false)
    }
  }, [selectedId, caseId])

  useEffect(() => {
    void reloadPreview()
    if (selected) {
      setFileName(`${selected.name.replace(/[^a-zA-Z0-9._\-\s]/g, '').trim() || 'documento'}.${format}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId])

  useEffect(() => {
    // Cuando cambia el formato, actualizamos la extensión.
    if (!fileName) return
    const noExt = fileName.replace(/\.(md|pdf|docx)$/i, '')
    setFileName(`${noExt}.${format}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format])

  const onApply = async () => {
    if (!selectedId) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/document-templates/${selectedId}/apply`, {
        caseId,
        format,
        fileName: fileName.trim() || undefined,
      })
      await onApplied()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open onClose={onClose} title="Aplicar plantilla al expediente" maxWidth="max-w-4xl">
      {loading ? (
        <div className="t-muted flex items-center gap-2 py-6">
          <Loader2 size={14} className="animate-spin" /> Cargando plantillas…
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-8">
          <FileText size={28} className="mx-auto t-faint mb-2" />
          <p className="t-muted text-sm">No hay plantillas creadas.</p>
          <a href="/settings/templates" className="text-toga-300 hover:underline text-sm">
            Crear la primera en Ajustes → Plantillas
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-[200px_1fr] gap-3">
          {/* Lista */}
          <aside className="border border-base rounded p-2 max-h-[500px] overflow-y-auto">
            <ul className="space-y-0.5">
              {templates.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className={[
                      'w-full text-left px-2 py-1.5 rounded text-sm',
                      t.id === selectedId
                        ? 'bg-toga-500/15 text-toga-200 border border-toga-500/30'
                        : 't-secondary hover:bg-hover border border-transparent',
                    ].join(' ')}
                  >
                    <div className="font-medium truncate">{t.name}</div>
                    <div className="text-[10px] t-faint">
                      {DocumentTemplateCategoryLabel[t.category]}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Preview + controls */}
          <div className="space-y-3 flex flex-col">
            <div className="border border-base rounded p-3 bg-slate-900/30 max-h-[400px] overflow-y-auto">
              <div className="text-[10px] uppercase tracking-wider t-faint mb-2 flex items-center gap-2">
                Vista previa con los datos de este expediente
                {previewLoading && <Loader2 size={10} className="animate-spin" />}
              </div>
              <div className="prose prose-sm prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview}</ReactMarkdown>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs t-muted">Formato</span>
                <select
                  value={format}
                  onChange={(e) => setFormat(e.target.value as RenderFormatT)}
                  className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5"
                >
                  <option value="pdf">PDF</option>
                  <option value="docx">Word (.docx)</option>
                  <option value="md">Markdown (.md)</option>
                </select>
              </label>
              <label className="block">
                <span className="text-xs t-muted">Nombre del archivo</span>
                <input
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  className="w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary mt-0.5 font-mono"
                />
              </label>
            </div>

            {error && <p className="text-xs text-rose-300">{error}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void onApply()}
                disabled={busy || !selectedId}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                Crear documento
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}
